import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
// tree-v2.css bevat de styling voor PersonEditor's event-modal,
// archive-search panel én de fan chart. Voorheen alleen geladen op /tree
// waardoor de modal op /people ongestyled was. Globaal importeren zodat
// alle pe-* / pm-* classes overal werken.
import './styles/tree-v2.css';
import { PecktreeLogo } from './components/shared/PecktreeLogo.tsx';
import { useSessionStore } from './stores/useSessionStore.ts';
import { useProjectStore } from './stores/useProjectStore.ts';
import { useDataStore } from './stores/useDataStore.ts';
import { useQualityStore } from './stores/useQualityStore.ts';
import { useNewQualityIssueToast } from './hooks/useNewQualityIssueToast.ts';
import { useEditorStore, type OpenEditorOptions } from './stores/useEditorStore.ts';
import { useUIStore } from './stores/useUIStore.ts';
import { useOnboardingStore } from './stores/useOnboardingStore.ts';
import AuthScreen from './components/screens/AuthScreen.tsx';
import CommandPalette from './components/shared/CommandPalette.tsx';
import LegalPages from './components/screens/LegalPages.tsx';
import ResetPasswordPage from './components/screens/ResetPasswordPage.tsx';
// Plan 4 (#778): /invite/:token is publiek — ook eager mounten zodat niet-
// ingelogde uitgenodigden de pagina te zien krijgen voordat AppRoutes (auth-
// vereist) wordt geladen.
import AcceptInvitePage from './components/screens/AcceptInvitePage.tsx';
import { useConfirm } from './components/shared/ModalDialogs.tsx';
import { useToast } from './components/shared/Toast.tsx';
import { kiesStartProject } from './utils/startProject.ts';
import AppSidebar from './components/layout/AppSidebar.tsx';
import AppTopbar from './components/layout/AppTopbar.tsx';
import AppRoutes, { PresenceContext } from './components/layout/AppRoutes.tsx';
import AppModals from './components/layout/AppModals.tsx';
import UpgradePrompt from './components/shared/UpgradePrompt.tsx';
import ProefAfscheid from './components/shared/ProefAfscheid.tsx';
import { limietUitleg, type TierLimiet } from './utils/limietUitleg.ts';
import { useInactivityTimeout } from './hooks/useInactivityTimeout.ts';
import { useConnectionStatus } from './hooks/useConnectionStatus.ts';
import {
  MembershipTier,
  Person,
  Project,
  PresenceUser,
  QualityIssue,
  QualityThresholds,
} from './types.ts';
import {
  ApiService,
  initializeBackendConfig,
  startProactiveRefresh,
  ensureSession,
} from './services/ApiService.ts';
import { SyncService } from './services/SyncService.ts';
import { isGlobaalGemeld } from './services/api/base.ts';
import { savePersonWithCache } from './services/dataSync.ts';
import type { RelatieItem } from './services/api/relationships.ts';
import { useTranslation } from './i18n.ts';
import { LAATST_GEZIENE_RELEASE, OUDE_SLEUTEL, moetWhatsNewTonen } from './utils/whatsNew.ts';
import { useCollaboration } from './hooks/useCollaboration.ts';
import { useTheme } from './hooks/useTheme.ts';
import { reportError } from './services/errorReporter.ts';

const App = () => {
  const { t, lang } = useTranslation();
  // useTheme must be called in App to initialize theme — sidebar reads it via its own hook call
  useTheme();
  const confirmModal = useConfirm();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tierLimiet, setTierLimiet] = useState<TierLimiet | null>(null);
  const location = useLocation();
  // #1775 — eenmalige toast als opslaan in de editor een nieuwe melding oplevert.
  useNewQualityIssueToast();
  // ── Zustand stores (granular selectors to avoid unnecessary re-renders) ──
  const user = useSessionStore((state) => state.user);
  const setUser = useSessionStore((state) => state.setUser);
  const loading = useSessionStore((state) => state.loading);
  const setLoading = useSessionStore((state) => state.setLoading);
  const pendingInvitations = useSessionStore((state) => state.pendingInvitations);
  const setPendingInvitations = useSessionStore((state) => state.setPendingInvitations);
  const appVersion = useSessionStore((state) => state.appVersion);
  const setAppVersion = useSessionStore((state) => state.setAppVersion);
  const announcements = useSessionStore((state) => state.announcements);
  const setAnnouncements = useSessionStore((state) => state.setAnnouncements);
  const dismissedAnnouncements = useSessionStore((state) => state.dismissedAnnouncements);
  const dismissAnnouncement = useSessionStore((state) => state.dismissAnnouncement);

  const currentProject = useProjectStore((state) => state.currentProject);
  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);
  const projects = useProjectStore((state) => state.projects);
  const setProjects = useProjectStore((state) => state.setProjects);
  const rootId = useProjectStore((state) => state.rootId);
  const setRootId = useProjectStore((state) => state.setRootId);

  const people = useDataStore((state) => state.people);
  const setPeople = useDataStore((state) => state.setPeople);
  const relationships = useDataStore((state) => state.relationships);
  const setRelationships = useDataStore((state) => state.setRelationships);
  const sources = useDataStore((state) => state.sources);
  const setSources = useDataStore((state) => state.setSources);
  const setEvents = useDataStore((state) => state.setEvents);
  const personStoryCount = useDataStore((state) => state.personStoryCount);
  const setPersonStoryCount = useDataStore((state) => state.setPersonStoryCount);
  const reviewCount = useDataStore((state) => state.reviewCount);
  const setReviewCount = useDataStore((state) => state.setReviewCount);

  // #1839 — App abonneert NIET op de editorstatus (isOpen/person): App rendert niets
  // daarvan, en elke wijziging liet App + het hele scherm eronder opnieuw renderen
  // (de kwaliteitslijst: 735 kaarten, 2-2,5 s). AppModals leest de editor zelf.
  const storeOpenEditor = useEditorStore((state) => state.openEditor);
  const storeCloseEditor = useEditorStore((state) => state.closeEditor);

  const sidebarOpen = useUIStore((state) => state.sidebarOpen);
  const setSidebarOpen = useUIStore((state) => state.setSidebarOpen);
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);
  const toggleSidebarCollapse = useUIStore((state) => state.toggleSidebarCollapse);
  const isCommandPaletteOpen = useUIStore((state) => state.isCommandPaletteOpen);
  const setIsCommandPaletteOpen = useUIStore((state) => state.setIsCommandPaletteOpen);
  const dashWidgetsUnlocked = useUIStore((state) => state.dashWidgetsUnlocked);
  const setDashWidgetsUnlocked = useUIStore((state) => state.setDashWidgetsUnlocked);
  const dashShowCatalog = useUIStore((state) => state.dashShowCatalog);
  const setDashShowCatalog = useUIStore((state) => state.setDashShowCatalog);
  const showCreateProjectModal = useUIStore((state) => state.showCreateProjectModal);
  const setShowCreateProjectModal = useUIStore((state) => state.setShowCreateProjectModal);
  const showProjectSwitcher = useUIStore((state) => state.showProjectSwitcher);
  const setShowProjectSwitcher = useUIStore((state) => state.setShowProjectSwitcher);
  const showWhatsNew = useUIStore((state) => state.showWhatsNew);
  const setShowWhatsNew = useUIStore((state) => state.setShowWhatsNew);
  const whatsNewData = useUIStore((state) => state.whatsNewData);
  const setWhatsNewData = useUIStore((state) => state.setWhatsNewData);
  // ── Local state (not in stores) ──
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [treeFocusId, setTreeFocusId] = useState<string | undefined>(undefined);
  const [createProjectLoading, setCreateProjectLoading] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlPersonId = searchParams.get('personId');

  // Viewer role: can view + comment, but NOT edit/add/delete
  const isViewer = currentProject?.memberRole === 'viewer';

  // Collaboration: single WebSocket instance for real-time presence + data broadcasts
  const {
    connected: wsConnected,
    presence: wsPresence,
    broadcastChange,
    onDataChanged: wsOnDataChanged,
  } = useCollaboration(currentProject?.id);

  // Remove loading screen after first render
  useEffect(() => {
    const loader = document.getElementById('pt-loader');
    if (loader) loader.remove();
  }, []);

  // HTTP fallback presence when WebSocket is disconnected
  const [httpPresence, setHttpPresence] = useState<PresenceUser[]>([]);
  useEffect(() => {
    if (wsConnected || !currentProject?.id) {
      setHttpPresence([]);
      return;
    }
    let cancelled = false;
    const poll = () => {
      ApiService.getPresence(currentProject.id)
        .then((data) => {
          if (!cancelled) setHttpPresence(data);
        })
        .catch((err) => console.warn('getPresence poll failed:', err.message || err));
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [wsConnected, currentProject?.id]);

  // Merge WS + HTTP presence and add isMe flag
  const mergedPresence = wsConnected ? wsPresence : httpPresence;
  const wsPresenceWithMe = useMemo(
    () => mergedPresence.map((p) => ({ ...p, isMe: p.email === user?.email })),
    [mergedPresence, user?.email]
  );

  // Listen for 403 permission-denied events dispatched by ApiService.
  // #1857 (audit B18) — een abonnementslimiet is geen rechtenprobleem: die krijgt een
  // venster met de echte reden, wat er wél blijft, en een weg naar de abonnementen.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<TierLimiet & { code?: string }>).detail;
      if (detail?.code === 'TIER_UPGRADE_REQUIRED') {
        setTierLimiet(detail);
        return;
      }
      toast(t('insufficientPermissions'), 'error');
    };
    window.addEventListener('pecktree:permission-denied', handler);
    return () => window.removeEventListener('pecktree:permission-denied', handler);
  }, [toast, t]);

  // Global unhandled error reporting
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      reportError(event.reason instanceof Error ? event.reason : String(event.reason));
    };
    const onError = (event: ErrorEvent) => {
      if (event.error) reportError(event.error);
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
    };
  }, []);

  // Listen for session-expired events — show toast and redirect to login
  useEffect(() => {
    let fired = false;
    const handler = () => {
      if (fired) return; // prevent multiple toasts
      fired = true;
      toast(t('sessionExpired'), 'error');
      // #466: gebruik centrale logout i.p.v. handmatige localStorage-manipulatie.
      // Zet ook de intent-flag zodat F5 na redirect niet alsnog silent-logt
      // in mocht één van de refresh-cookies nog onverhoopt geldig blijken.
      // Niet awaited — de 1.5s toast-delay is leidend voor de UX.
      ApiService.logout();
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    };
    window.addEventListener('pecktree:session-expired', handler);
    return () => window.removeEventListener('pecktree:session-expired', handler);
  }, [toast, t]);

  // Track editing person ID via ref to avoid stale closure in closeEditor
  const editingPersonIdRef = useRef<string | null>(null);

  // Broadcast editing state to collaborators
  const openEditor = useCallback(
    (person: Person | null, genderOrOptions?: 'M' | 'F' | 'U' | OpenEditorOptions) => {
      storeOpenEditor(person, genderOrOptions);
      editingPersonIdRef.current = person?.id || null;
      if (person?.id) broadcastChange('editing_person', { personId: person.id });
    },
    [broadcastChange, storeOpenEditor]
  );
  const closeEditor = useCallback(() => {
    if (editingPersonIdRef.current)
      broadcastChange('done_editing_person', { personId: editingPersonIdRef.current });
    editingPersonIdRef.current = null;
    storeCloseEditor();
  }, [broadcastChange, storeCloseEditor]);

  // Collaboration: auto-refresh data when other users make changes (debounced)
  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventsRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const cleanup = wsOnDataChanged((event: { type: string; data?: unknown }) => {
      const projectId = currentProject?.id;
      if (!projectId) return;
      const { type } = event;

      // People/relationship changes → debounced refresh
      if (
        [
          'person_updated',
          'person_created',
          'person_deleted',
          'relationship_created',
          'relationship_deleted',
        ].includes(type)
      ) {
        if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = setTimeout(async () => {
          try {
            const [freshPeople, freshRels] = await Promise.all([
              ApiService.getPeople(projectId),
              ApiService.getRelationships(projectId),
            ]);
            setPeople(freshPeople, projectId);
            setRelationships(freshRels);
          } catch (err) {
            console.error('Auto-refresh failed:', err);
          }
        }, 2000);
      }

      // Event changes → debounced events-cache refresh (multi-user sync)
      if (['event_added', 'event_updated', 'event_removed'].includes(type)) {
        if (eventsRefreshDebounceRef.current) clearTimeout(eventsRefreshDebounceRef.current);
        eventsRefreshDebounceRef.current = setTimeout(async () => {
          try {
            const freshEvents = await ApiService.getEvents(projectId);
            useDataStore.getState().setEvents(freshEvents, projectId);
          } catch (err) {
            console.error('Events auto-refresh failed:', err);
          }
        }, 2000);
      }

      // Member changes → refresh projects list
      if (['member_added', 'member_removed', 'member_role_changed'].includes(type)) {
        ApiService.getProjectsFlat()
          .then(setProjects)
          .catch((err) => console.warn('getProjects (member change) failed:', err.message || err));
      }
    });
    return () => {
      cleanup();
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      if (eventsRefreshDebounceRef.current) clearTimeout(eventsRefreshDebounceRef.current);
    };
  }, [wsOnDataChanged, currentProject?.id]);

  // Inactivity timeout: auto-logout after 30 minutes of no activity
  // #466: await de server-logout zodat cookies echt gewist zijn voordat we
  // navigeren. Anders kan een F5 direct hierna via refresh-token automatisch
  // weer inloggen (refresh cookies nog geldig op server).
  const handleInactivityLogout = useCallback(async () => {
    broadcastLogout(); // Notify other tabs
    await ApiService.logout();
    setUser(null);
    navigate('/');
  }, [navigate]);
  const {
    showWarning: showInactivityWarning,
    remainingSeconds,
    dismissWarning,
    broadcastLogout,
  } = useInactivityTimeout({
    timeoutMs: 30 * 60 * 1000,
    warningMs: 5 * 60 * 1000,
    onTimeout: handleInactivityLogout,
    enabled: !!user,
  });

  // Connection awareness: offline/reconnecting banner + session recovery
  const { state: connectionState, wasOffline, clearWasOffline } = useConnectionStatus();

  // Session recovery: when tab becomes visible, ensure JWT is still valid
  useEffect(() => {
    if (!user) return;
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      const sessionOk = await ensureSession();
      if (!sessionOk) {
        toast(t('sessionExpired'));
        // #466: await zodat server-cookies echt gewist zijn voor navigate.
        await ApiService.logout();
        setUser(null);
        navigate('/');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user, toast, t, navigate]);

  // Data refresh after reconnect: silently reload people + relationships
  useEffect(() => {
    if (!wasOffline || !currentProject?.id) return;
    clearWasOffline();
    toast(t('connectionRestored'));
    // Silent data refresh
    Promise.all([
      ApiService.getPeople(currentProject.id),
      ApiService.getRelationships(currentProject.id),
    ])
      .then(([p, r]) => {
        setPeople(p, currentProject.id);
        setRelationships(r);
      })
      .catch((err) => console.warn('reconnect data refresh failed:', err.message || err));
  }, [wasOffline, currentProject?.id, clearWasOffline, toast, t]);

  // Sync feedback: listen for sync complete/failed events
  useEffect(() => {
    const handleSyncComplete = (e: Event) => {
      const count = (e as CustomEvent).detail?.count || 0;
      if (count > 0) toast(t('syncComplete'));
    };
    const handleSyncFailed = (e: Event) => {
      const count = (e as CustomEvent).detail?.count || 0;
      if (count > 0) toast(t('syncFailed').replace('{count}', String(count)), 'warning');
    };
    const handleInvitationAccepted = () => {
      // Refresh projects list after invite-accept zodat het nieuwe project zichtbaar is (Refs #664).
      ApiService.getProjectsFlat()
        .then(setProjects)
        .catch((err) =>
          console.warn('getProjects (invitation accepted) failed:', err.message || err)
        );
    };
    window.addEventListener('pt-sync-complete', handleSyncComplete);
    window.addEventListener('pt-sync-failed', handleSyncFailed);
    window.addEventListener('pt-invitation-accepted', handleInvitationAccepted);
    return () => {
      window.removeEventListener('pt-sync-complete', handleSyncComplete);
      window.removeEventListener('pt-sync-failed', handleSyncFailed);
      window.removeEventListener('pt-invitation-accepted', handleInvitationAccepted);
    };
  }, [toast, t, setProjects]);

  // Close PersonEditor panel on page change (not on query param updates)
  // #736 — selectedPersonId NIET meer clearen bij route-change; de gele omlijning
  // van de laatst-geselecteerde persoon blijft behouden tussen routes heen en
  // wordt hersteld bij terugkeer naar /tree. Editor sluit wel expliciet.
  useEffect(() => {
    if (useEditorStore.getState().isOpen) closeEditor();
    // #735 — persist de huidige pathname voor de volgende navigatie. useTreeState
    // leest deze waarde bij mount om te bepalen of we vanuit een andere route
    // naar /tree komen (reset view naar PEDIGREE) of binnen /tree blijven (behoud).
    try {
      sessionStorage.setItem('pt_last_pathname', location.pathname);
    } catch {
      /* ignore */
    }
  }, [location.pathname]);

  useEffect(() => {
    // #736 — alleen setten bij expliciete urlPersonId; anders blijft de eerder
    // geselecteerde persoon (uit localStorage of vorige click) staan.
    if (urlPersonId) {
      setSelectedPersonId(urlPersonId);
      setTreeFocusId(urlPersonId);
      // Track recently viewed persons for dashboard widget
      try {
        const key = 'pt-recent-persons';
        const stored = localStorage.getItem(key);
        const ids: string[] = stored ? JSON.parse(stored) : [];
        const filtered = ids.filter((id) => id !== urlPersonId);
        filtered.unshift(urlPersonId);
        localStorage.setItem(key, JSON.stringify(filtered.slice(0, 12)));
      } catch {
        /* ignore */
      }
    } else {
      // NC #841 — zonder ?personId (bv. "Stamboom" in de zijbalk) opent de boom
      // zoals ingesteld; een eerder gevraagde persoon blijft niet hangen.
      setTreeFocusId(undefined);
    }
  }, [urlPersonId]);

  // #736 — synchroniseer App-level selectedPersonId met de door useTreeState
  // gepersisteerde waarde. Zonder deze useEffect blijft App.tsx op null staan
  // na F5/reload terwijl de tree-view Gerarda gerestored heeft — dan is de
  // DetailPanel-context leeg terwijl de gele omlijning wél zichtbaar is.
  // Alleen overnemen als App nog geen expliciete waarde heeft (URL/save).
  useEffect(() => {
    if (!currentProject?.id || selectedPersonId) return;
    try {
      const stored = localStorage.getItem(`pt_last_selected_${currentProject.id}`);
      if (stored) setSelectedPersonId(stored);
    } catch {
      /* ignore */
    }
  }, [currentProject?.id, selectedPersonId]);

  const init = async (targetProjectId?: string, forceRootId?: string) => {
    // Initialize backend config first to determine if we're in sandbox mode
    const backendConfig = await initializeBackendConfig();

    try {
      // #466: als de user expliciet is uitgelogd (handmatig, inactivity, of
      // sessie-verloren), skip de auto-refresh flow. Anders zou een F5 na
      // een gefaalde logout-call de user automatisch weer inloggen via de
      // nog-aanwezige refresh-token cookie. We wissen de flag direct en
      // vragen de user om opnieuw in te loggen.
      const intentLoggedOut = (() => {
        try {
          return localStorage.getItem('af_intent_logged_out') === '1';
        } catch {
          return false;
        }
      })();
      if (intentLoggedOut) {
        try {
          localStorage.removeItem('af_intent_logged_out');
        } catch {
          /* ignore */
        }
        setLoading(false);
        return;
      }

      // Check authentication via httpOnly cookie (try refresh if expired)
      let authRes = await fetch('/api/auth/me', { credentials: 'include' });
      if (authRes.status === 401) {
        // Access token expired — attempt silent refresh via refresh token cookie
        const refreshRes = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (refreshRes.ok) {
          authRes = await fetch('/api/auth/me', { credentials: 'include' });
        }
      }
      if (!authRes.ok) {
        setLoading(false);
        return; // Not authenticated
      }
      const { user: serverUser } = await authRes.json();
      localStorage.setItem('af_user', JSON.stringify(serverUser));
      setUser(serverUser);
      startProactiveRefresh();

      // Load onboarding state (non-blocking)
      useOnboardingStore.getState().load();

      // Google Maps wordt NIET meer hier geladen.
      //
      // De SDK haalt zelf lettertypes op bij `fonts.googleapis.com` (Google Sans,
      // Roboto, Material Symbols). Door hem direct na het inloggen te laden ging
      // het IP-adres van élke gebruiker naar Google zodra hij inlogde, op elk
      // scherm, ook zonder kaart in beeld — precies wat `43-self-hosted-assets`
      // ("no external CDN requests on dashboard load") al die tijd rood meldde in
      // een testproject dat nergens draaide.
      //
      // De schermen die een kaart tonen roepen `__loadGoogleMaps()` nu zelf aan:
      // PlaceManager en het kaart-widget op het dashboard.

      if (backendConfig.version) setAppVersion(backendConfig.version);

      // NC #832 — "Wat is er nieuw" op de release-tag, niet op de app-versie
      // (die springt bij elke merge). Server cachet de release 30 min.
      fetch('/api/releases/latest', { credentials: 'include' })
        .then((r) => r.json())
        .then((releaseData) => {
          if (!releaseData.available || !releaseData.version) return;
          const opgeslagen = localStorage.getItem(LAATST_GEZIENE_RELEASE);
          if (moetWhatsNewTonen(releaseData.version, opgeslagen) && releaseData.body) {
            setWhatsNewData({ version: releaseData.version, body: releaseData.body });
            setShowWhatsNew(true);
          }
          localStorage.setItem(LAATST_GEZIENE_RELEASE, releaseData.version);
          localStorage.removeItem(OUDE_SLEUTEL);
        })
        .catch((err) => console.warn('fetch latest release failed:', err.message || err));

      const projs = await ApiService.getProjectsFlat();
      setProjects(projs);

      // Fetch pending invitations for badge + project switcher
      ApiService.getPendingInvitations()
        .then((invs) => setPendingInvitations(invs))
        .catch(() => setPendingInvitations([]));

      // Prioriteit: targetProjectId (van import) > URL ?project= param > DB (cross-device) > localStorage > eerste project
      const urlProjectId = new URLSearchParams(window.location.search).get('project');
      const lastProjId =
        targetProjectId ||
        urlProjectId ||
        serverUser.lastProjectId ||
        localStorage.getItem(`af_current_project_${serverUser.id}`) ||
        localStorage.getItem('af_current_project');
      // #1591 — ook een gearchiveerd project blijft open na herladen; alleen als het
      // echt weg is het eerste project, en dat melden (niet stil wisselen).
      const { project: activeProj, teruggevallen } = await kiesStartProject(projs, lastProjId, () =>
        ApiService.getProjectsFlat({ include_archived: true })
      );
      if (teruggevallen && activeProj)
        toast(t('startProjectNietBeschikbaar', { name: activeProj.name }), 'info');
      // Clean up ?project= param from URL after reading
      if (urlProjectId) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      if (activeProj) {
        await handleSelectProject(activeProj, forceRootId);
      }
    } catch (e) {
      // Don't call ApiService.logout() here — a failed init (e.g. network error
      // during project load) doesn't mean the session is invalid. Calling logout
      // would revoke the JWT server-side, breaking parallel browser sessions
      // that share the same token (e.g. in E2E tests with storageState).
      console.error('Init failed:', e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  // Listen for wizard completion — activates the newly created project via
  // the proper handleSelectProject flow (with hook-bound setters)
  useEffect(() => {
    const handler = async (e: Event) => {
      const { projectId } = (e as CustomEvent).detail;
      if (!projectId) return;
      try {
        const projs = await ApiService.getProjectsFlat();
        setProjects(projs);
        const proj = projs.find((p: Project) => p.id === projectId);
        if (proj) await handleSelectProject(proj);
      } catch (err) {
        console.error('wizard-complete handler failed:', err);
      }
    };
    window.addEventListener('pecktree:wizard-complete', handler);
    return () => window.removeEventListener('pecktree:wizard-complete', handler);
  }, [user]);

  // Fetch active announcements
  useEffect(() => {
    if (!user) return;
    fetch('/api/announcements/active', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setAnnouncements(data))
      .catch((err) => console.warn('fetch announcements failed:', err.message || err));
  }, [user]);

  const activeAnnouncements = announcements.filter((a) => !dismissedAnnouncements.has(a.id));

  // Start SyncService on mount
  useEffect(() => {
    SyncService.start();

    // Subscribe to queue size changes
    const unsubscribe = SyncService.subscribe((size) => {
      setQueueSize(size);
    });

    return () => {
      SyncService.stop();
      unsubscribe();
    };
  }, []);

  // reviewCount is set in handleSelectProject and updated via WebSocket place_geocoded events
  // No need to re-fetch places on every route change

  // Centralized quality analysis — debounced to avoid expensive recomputation on every keystroke
  const qualityEmptyResult = useRef({
    issues: [] as QualityIssue[],
    indicators: new Map<string, { errors: number; warnings: number }>(),
    duplicateIds: new Set<string>(),
    issueCount: 0,
  }).current;
  const [qualityAnalysis, setQualityAnalysis] = useState(qualityEmptyResult);
  const qualityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qualityWorkerRef = useRef<Worker | null>(null);

  // Spawn quality Web Worker once and clean up on unmount
  useEffect(() => {
    const worker = new Worker(new URL('./workers/qualityWorker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent) => {
      if (e.data.error) {
        console.error('Quality worker error:', e.data.error);
        return;
      }
      const { issues, indicators, duplicateIds, issueCount, seq } = e.data;
      // #1775 — dezelfde analyse delen met detailpaneel, stamboom en editor.
      useQualityStore.getState().setAnalysis(issues, seq);
      setQualityAnalysis({
        issues,
        indicators,
        duplicateIds: new Set(duplicateIds),
        issueCount,
      });
    };
    qualityWorkerRef.current = worker;
    return () => worker.terminate();
  }, []);

  const qualityChecks = (currentProject?.settings as Record<string, unknown> | undefined)
    ?.qualityChecks as
    { thresholds?: Partial<QualityThresholds>; disabledCategories?: string[] } | undefined;

  // Debounced quality analysis — offloaded to Web Worker
  useEffect(() => {
    if (people.length === 0 || people.length > 2000) {
      setQualityAnalysis(qualityEmptyResult);
      useQualityStore.getState().setAnalysis([]);
      return;
    }
    if (qualityDebounceRef.current) clearTimeout(qualityDebounceRef.current);
    qualityDebounceRef.current = setTimeout(() => {
      const acked: string[] =
        ((currentProject?.settings as Record<string, unknown> | undefined)
          ?.acknowledgedIssues as string[]) || [];
      if (qualityWorkerRef.current) {
        qualityWorkerRef.current.postMessage({
          people,
          relationships,
          acknowledgedIssues: acked,
          // #1942 (NC #879) — de drempels van de pagina Datakwaliteit gelden ook hier.
          thresholds: qualityChecks?.thresholds,
          disabledCategories: qualityChecks?.disabledCategories,
          seq: useQualityStore.getState().markPosted(),
        });
      }
    }, 2500);
    return () => {
      if (qualityDebounceRef.current) clearTimeout(qualityDebounceRef.current);
    };
  }, [people, relationships, currentProject?.id, qualityChecks]);

  // M3 — Ctrl+K keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(!useUIStore.getState().isCommandPaletteOpen);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // L2 — Dynamic page title
  useEffect(() => {
    // #1773 — paginanaam in de taal van de gebruiker (sleutels zoals het menu).
    const pageTitleKeys: Record<string, string> = {
      '/': 'dashboard',
      '/tree': 'tree',
      '/people': 'people',
      '/sources': 'sources',
      '/places': 'places',
      '/stories': 'stories',
      '/reports': 'reports',
      '/account': 'settings',
      '/search': 'search',
      '/import': 'importExport',
      '/audit': 'auditLog',
      '/media': 'media',
      '/quality': 'dataQuality',
      '/relationships': 'relationships',
      '/family-sheet': 'familyGroupSheet',
      '/planner': 'planner',
      '/timeline': 'timeline',
      '/roadmap': 'roadmap',
      '/analyse': 'statistics',
      '/support': 'support',
      '/deleted': 'trash',
      '/organisatie': 'organization',
    };
    const titleKey = pageTitleKeys[location.pathname];
    const title = titleKey ? t(titleKey) : 'Pecktree';
    document.title = `${title} — Pecktree`;
  }, [location.pathname, t]);

  const handleSelectProject = async (proj: Project, forceRootId?: string) => {
    setCurrentProject(proj);
    localStorage.setItem('af_current_project', proj.id);
    if (user) localStorage.setItem(`af_current_project_${user.id}`, proj.id);
    // Persist to DB for cross-device restore (fire-and-forget)
    ApiService.setLastProject(proj.id);

    // Ensure project is in the projects list, and update if it already exists
    const prevProjects = useProjectStore.getState().projects;
    const projIdx = prevProjects.findIndex((p) => p.id === proj.id);
    if (projIdx >= 0) {
      const updated = [...prevProjects];
      updated[projIdx] = proj;
      setProjects(updated);
    } else {
      setProjects([...prevProjects, proj]);
    }

    // Progressive loading: people + relationships first (needed for TreeView/Dashboard),
    // then sources + places async (non-blocking)
    const [p, r] = await Promise.all([
      ApiService.getPeople(proj.id),
      ApiService.getRelationships(proj.id),
    ]);

    setPeople(p, proj.id);
    setRelationships(r);

    // B-58 — de terugval op `p[0]` blijft staan zodat de boom iets te tekenen
    // heeft, maar hij mag niet meer als PROBAND worden gepresenteerd. `p[0]` komt
    // uit `ORDER BY sosa_number NULLS LAST, last_name, first_name`, en
    // `sosa_number` is vrijwel overal leeg. Na een verse GEDCOM-import van 4000
    // personen opende de kwartierstaat dus op "Aalbers, Adriana" met het label
    // PROBAND, alsof de app die keuze had gemaakt.
    //
    // De echte proband komt uit `currentProject.proband_id`; is die leeg, dan is
    // er geen proband en zegt de boom dat ook (zie FamilyTreePage).
    if (forceRootId) setRootId(forceRootId);
    else if (proj.proband_id) setRootId(proj.proband_id);
    else if (p.length > 0) setRootId(p[0].id);

    // Non-blocking: sources + places + stories + events load in background
    ApiService.getSources(proj.id).then((s) => setSources(s));
    ApiService.getPlaces(proj.id).then((places) => {
      setReviewCount(places.filter((pl: { needsReview?: boolean }) => pl.needsReview).length);
    });
    ApiService.getEvents(proj.id)
      .then((events) => setEvents(events, proj.id))
      .catch((err) => console.warn('getEvents failed:', err?.message || err));
    refreshStoryCount(proj.id);
  };

  // Notification deep-link: switch project + navigate when NotificationBell
  // dispatches 'pecktree:notification-navigate'. Uses a ref to handleSelectProject
  // so the listener stays attached across renders without stale closures. Refs #614.
  const selectProjectRef = useRef(handleSelectProject);
  useEffect(() => {
    selectProjectRef.current = handleSelectProject;
  });
  // #1839 — stabiele referentie voor de (gememoïseerde) AppRoutes.
  const stableSelectProject = useCallback(
    (proj: Project, forceRootId?: string) => selectProjectRef.current(proj, forceRootId),
    []
  );
  useEffect(() => {
    const handler = async (e: Event) => {
      const { projectId, route } = (e as CustomEvent).detail || {};
      if (!projectId || !route) return;
      const target = useProjectStore.getState().projects.find((p) => p.id === projectId);
      if (target) {
        try {
          await selectProjectRef.current(target);
        } catch (err) {
          console.error('Failed to switch project from notification:', err);
        }
      }
      navigate(route);
    };
    window.addEventListener('pecktree:notification-navigate', handler);
    return () => window.removeEventListener('pecktree:notification-navigate', handler);
  }, [navigate]);

  // Stable callbacks for ProjectSwitcher (prevents re-renders)
  const handleCloseSwitcher = useCallback(() => setShowProjectSwitcher(false), []);
  const handleSwitcherSelect = useCallback((p: Project) => {
    handleSelectProject(p);
    setShowProjectSwitcher(false);
  }, []);
  const handleSwitcherCreate = useCallback(() => {
    setShowProjectSwitcher(false);
    setShowCreateProjectModal(true);
  }, []);
  const handleSwitcherImport = useCallback(() => {
    setShowProjectSwitcher(false);
    navigate('/import');
  }, [navigate]);

  const refreshStoryCount = (projId: string) => {
    ApiService.getStories(projId)
      .then((stories) => {
        const counts = new Map<string, number>();
        for (const story of stories) {
          for (const pid of story.taggedPersonIds || []) {
            counts.set(pid, (counts.get(pid) || 0) + 1);
          }
        }
        setPersonStoryCount(counts);
      })
      .catch((err) => console.warn('refreshStoryCount failed:', err.message || err));
  };

  const handleSetProband = async (personId: string, personName?: string) => {
    if (!currentProject) return;
    if (
      !(await confirmModal({
        title: t('setAsProband'),
        message: t('setProbandConfirm').replace('{name}', personName || ''),
      }))
    )
      return;
    try {
      await ApiService.updateProject(currentProject.id, { proband_id: personId });
      setRootId(personId);
      setCurrentProject({ ...currentProject, proband_id: personId });
    } catch (err) {
      console.error('Failed to set proband:', err);
    }
  };

  // #1839 — stabiele referentie voor de (gememoïseerde) AppRoutes; roept altijd de
  // nieuwste handleSetProband aan (die leest currentProject uit de render).
  const setProbandRef = useRef(handleSetProband);
  useEffect(() => {
    setProbandRef.current = handleSetProband;
  });
  const stableSetProband = useCallback(
    (personId: string, personName?: string) => setProbandRef.current(personId, personName),
    []
  );

  const handleCreateProject = async (
    name: string,
    description: string,
    researchType: string,
    organizationId?: string | null,
    extras?: {
      generations?: number | null;
      startDate?: string | null;
      expectedDeliveryDate?: string | null;
    }
  ) => {
    setCreateProjectLoading(true);
    try {
      const settings: Record<string, unknown> = {
        privacy: { redactYears: 100 },
        researchType,
      };
      if (extras?.generations != null) {
        // NC #605 — onder settings.display.maxGenerations schrijven: dat is de sleutel
        // die het dashboard leest (Dashboard.tsx) én waar de inline-edit naar schrijft.
        // Plat settings.maxGenerations werd nooit getoond.
        settings.display = { maxGenerations: extras.generations };
      }
      const newProject = await ApiService.createProject(name, {
        description,
        settings,
        organizationId: organizationId ?? null,
        startDate: extras?.startDate ?? null,
        expectedDeliveryDate: extras?.expectedDeliveryDate ?? null,
      });
      setProjects([...useProjectStore.getState().projects, newProject]);
      handleSelectProject(newProject);
      setShowCreateProjectModal(false);
      navigate('/');
    } catch (err) {
      // #1857 — een 403 (bv. de projectlimiet) meldt de globale handler al met uitleg.
      if (isGlobaalGemeld(err)) return;
      toast(
        t('createProjectFailed') + (err instanceof Error ? err.message : t('unknownError')),
        'error'
      );
    } finally {
      setCreateProjectLoading(false);
    }
  };

  const handleDeletePerson = useCallback(async (personId: string) => {
    try {
      await ApiService.deletePerson(personId);
      setPeople(useDataStore.getState().people.filter((p) => p.id !== personId));
      setRelationships(
        useDataStore
          .getState()
          .relationships.filter((r) => r.personA !== personId && r.personB !== personId)
      );
    } catch (err) {
      console.error('Failed to delete person:', err);
    }
  }, []);

  const handleDeleteRelationship = useCallback(
    async (relId: string) => {
      try {
        await ApiService.deleteRelationship(relId);
        if (currentProject?.id) {
          const freshRels = await ApiService.getRelationships(currentProject.id);
          setRelationships(freshRels);
        }
      } catch (err) {
        console.error('Failed to delete relationship:', err);
      }
    },
    [currentProject?.id]
  );

  const handleBulkDeletePeople = useCallback((personIds: string[]) => {
    const idSet = new Set(personIds);
    const { people: curPeople, relationships: curRels } = useDataStore.getState();
    setPeople(curPeople.filter((p) => !idSet.has(p.id)));
    setRelationships(curRels.filter((r) => !idSet.has(r.personA) && !idSet.has(r.personB)));
  }, []);

  // Shared data-refresh callback — used by tree, people, quality, PersonEditor, etc.
  //
  // #882: archief-import maakt nieuwe events, sources, en relationships aan
  // (plus people voor secundaire personen). Pre-fix verfreste deze callback
  // alleen people + relationships — events & sources bleven stale tot F5.
  // Nu wordt ook events & sources geverfresht zodat na import alle views
  // (Overview-timeline, Family-tab, PersonEditor->Events, tree, statistieken)
  // direct de nieuwe data tonen.
  const refreshPeopleAndRelationships = useCallback(async () => {
    if (!currentProject?.id) return;
    const [p, r, e, s] = await Promise.all([
      ApiService.getPeople(currentProject.id),
      ApiService.getRelationships(currentProject.id),
      ApiService.getEvents(currentProject.id),
      ApiService.getSources(currentProject.id),
    ]);
    setPeople(p, currentProject.id);
    setRelationships(r);
    setEvents(e, currentProject.id);
    setSources(s);
  }, [currentProject?.id]);

  // Navigate to a person in the tree view
  const navigateToPersonInTree = useCallback(
    (id: string) => {
      setTreeFocusId(id);
      navigate(`/tree?personId=${id}`);
    },
    [navigate]
  );

  // Tree node click handler
  const handleTreeNodeClick = useCallback(
    (p: Person) => {
      setSelectedPersonId(p.id);
      setTreeFocusId(p.id);
      navigate(`/tree?personId=${p.id}`);
    },
    [navigate]
  );

  // Open editor for a new person (Dashboard + PersonManager)
  const handleAddPerson = useCallback(() => openEditor(null), [openEditor]);

  // Stories changed → refresh story counts
  const handleStoriesChanged = useCallback(() => {
    if (currentProject?.id) refreshStoryCount(currentProject.id);
  }, [currentProject?.id]);

  // GEDCOM import success → re-init with new project/root
  const handleImportSuccess = useCallback((pid: string, rid?: string) => {
    init(pid, rid || '');
  }, []);

  // Restore from trash → refresh people, relationships, AND sources
  const handleRestoreDeleted = useCallback(async () => {
    if (!currentProject?.id) return;
    const [p, r, s] = await Promise.all([
      ApiService.getPeople(currentProject.id),
      ApiService.getRelationships(currentProject.id),
      ApiService.getSources(currentProject.id),
    ]);
    setPeople(p, currentProject.id);
    setRelationships(r);
    setSources(s);
  }, [currentProject?.id]);

  // Quality checker: navigate to person in editor
  const handleQualityNavigateToPerson = useCallback(
    (id: string) => {
      const person = people.find((p) => p.id === id);
      if (person) openEditor(person);
    },
    [people, openEditor]
  );

  // Logout handler for sidebar — #466: await server-logout voordat navigate.
  const handleLogout = useCallback(async () => {
    await ApiService.logout();
    setUser(null);
    navigate('/');
  }, [navigate]);

  // PersonEditor save handler (passed to AppModals)
  // Gebruikt savePersonWithCache zodat useDataStore direct wordt bijgewerkt:
  // alle consumers (DetailPanel, fan chart, lists) zien de wijziging zonder reload.
  // De full-refetch blijft als safety-net voor relationship-wijzigingen.
  const handleSavePerson = useCallback(
    async (
      data: Partial<Person>,
      opts?: { clientId?: string; relationships?: RelatieItem[]; zelfMelden?: boolean }
    ) => {
      const saved = await savePersonWithCache({ ...data, projectId: currentProject?.id }, opts);
      // #1825 — de nieuwe persoon blijft open in de editor: vanaf nu "bewerkt" hij hem.
      if (!data.id && saved?.id) {
        editingPersonIdRef.current = saved.id;
        broadcastChange('editing_person', { personId: saved.id });
      }
      // Auto-set first person as proband
      if (!currentProject?.proband_id && people.length === 0 && saved?.id && currentProject) {
        // #1361 — de persoon staat op dit punt al in de database. Faalt het
        // vastleggen van de proband, dan mag dat de geslaagde opslag niet
        // meeslepen: zonder deze afvang gooit de hele save en denkt de gebruiker
        // dat zijn invoer weg is, terwijl die er gewoon staat.
        try {
          await ApiService.updateProject(currentProject.id, { proband_id: saved.id });
          setRootId(saved.id);
          setCurrentProject({ ...currentProject, proband_id: saved.id });
        } catch (err) {
          console.warn('Proband automatisch vastleggen mislukt:', err);
          toast(t('probandAutoSetFailed'), 'warning');
        }
      }
      // Incremental refresh van relationships (savePersonWithCache updatet alleen people-cache)
      if (currentProject?.id) {
        ApiService.getRelationships(currentProject.id)
          .then(setRelationships)
          .catch((err) => console.warn('getRelationships after save failed:', err?.message || err));
      }
      // Broadcast to collaborators
      if (saved?.id) {
        broadcastChange(data.id ? 'person_updated' : 'person_created', {
          id: saved.id,
          name: `${saved.givenNames || ''} ${saved.surname || ''}`.trim(),
        });
      }
      return saved; // Return for PersonEditor to handle new-person event/media linking, then close
    },
    [currentProject, people.length, broadcastChange, setCurrentProject, setRootId, setRelationships]
  );

  // Project deleted handler for ProjectSwitcher
  const handleProjectDeleted = useCallback(() => {
    ApiService.getProjectsFlat()
      .then(async (projs) => {
        setProjects(projs);
        if (!currentProject) return;
        // Is het huidige project weg, dan het eerste. #1591 — een open gearchiveerd
        // project staat niet in deze lijst, maar bestaat nog: niet wegwisselen.
        const { project } = await kiesStartProject(projs, currentProject.id, () =>
          ApiService.getProjectsFlat({ include_archived: true })
        );
        if (!project) setCurrentProject(null);
        else if (project.id !== currentProject.id) handleSelectProject(project);
      })
      .catch((err) => console.warn('getProjects (after delete) failed:', err.message || err));
  }, [currentProject]);

  // Project updated handler for ProjectSwitcher
  const handleProjectUpdated = useCallback(
    (updated: Partial<Project> & { id: string }) => {
      setProjects(
        useProjectStore
          .getState()
          .projects.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
      );
      if (currentProject?.id === updated.id) {
        const prev = useProjectStore.getState().currentProject;
        setCurrentProject(prev ? { ...prev, ...updated } : prev);
      }
    },
    [currentProject?.id]
  );

  // Invitation handled → refresh invitations + projects
  const handleInvitationHandled = useCallback(() => {
    ApiService.getPendingInvitations()
      .then((invs) => setPendingInvitations(invs))
      .catch(() => setPendingInvitations([]));
    ApiService.getProjectsFlat()
      .then((projs) => setProjects(projs))
      .catch((err) => console.warn('getProjects (invitation handled) failed:', err.message || err));
  }, []);

  if (loading) {
    return (
      <div className="h-screen bg-pt-forest dark:bg-[#0f1a14] flex flex-col items-center justify-center gap-5">
        <div className="flex items-center gap-3">
          <PecktreeLogo size={48} />
          <span className="text-[28px] font-semibold tracking-tight leading-none">
            <span className="text-[#F0DEB3] dark:text-[#F0DEB3]">Peck</span>
            <span className="text-pt-gold-text dark:text-[var(--gold-text)]">tree</span>
          </span>
        </div>
        <div className="w-[100px] h-[2px] rounded-sm overflow-hidden bg-white/10 dark:bg-white/6">
          <div className="w-[30%] h-full rounded-sm bg-pt-gold-text animate-[pt-slide_0.9s_ease-in-out_infinite]" />
        </div>
      </div>
    );
  }

  // Legal pages are publicly accessible without authentication.
  // Plan 4 (#778): /invite/:token is ook publiek — niet-ingelogde uitgenodigden
  // moeten de invite-detail kunnen zien én via mini-register kunnen aanmaken.
  const currentPath = location.pathname;
  if (
    !user &&
    (currentPath === '/privacy' ||
      currentPath === '/terms' ||
      currentPath.startsWith('/reset-password') ||
      /^\/invite\/[^/]+$/.test(currentPath))
  ) {
    return (
      <Routes>
        <Route path="/privacy" element={<LegalPages page="privacy" lang={lang} />} />
        <Route path="/terms" element={<LegalPages page="terms" lang={lang} />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
      </Routes>
    );
  }

  // Store invitation token for post-login redirect (localStorage survives verify page reload)
  if (!user && currentPath.startsWith('/invite')) {
    const inviteSearchParams = window.location.search.substring(1);
    if (inviteSearchParams) {
      localStorage.setItem('pending_invite_params', inviteSearchParams);
      localStorage.setItem('pending_invite_ts', String(Date.now()));
    }
  }

  // Geen gebruiker? Toon AuthScreen
  if (!user) {
    return (
      <AuthScreen
        onSuccess={(u) => {
          // Clear stale project/data state from previous session
          useProjectStore.getState().setCurrentProject(null);
          useProjectStore.getState().setRootId('');
          useDataStore.getState().setPeople([], null);
          useDataStore.getState().setRelationships([]);
          setUser(u);
          init();
          // Check for pending invitation after login (24h TTL)
          const pendingInvite = localStorage.getItem('pending_invite_params');
          const pendingTs = Number(localStorage.getItem('pending_invite_ts') || '0');
          const isExpired = Date.now() - pendingTs > 24 * 60 * 60 * 1000;
          if (pendingInvite && !isExpired) {
            localStorage.removeItem('pending_invite_params');
            localStorage.removeItem('pending_invite_ts');
            navigate(`/invite?${pendingInvite}`);
            return;
          }
          // Cleanup stale invite data
          localStorage.removeItem('pending_invite_params');
          localStorage.removeItem('pending_invite_ts');
          // #73 (ultra-UX-audit): 2FA is aanbevolen, niet verplicht — geen
          // gedwongen redirect meer naar /account bij Business zonder 2FA.
          // De nudge leeft op de security-tab (TwoFactorSetup-banner).
        }}
      />
    );
  }

  return (
    <div className="app-shell">
      {/* Skip-to-content */}
      <a href="#main-content" className="skip-link">
        {t('skipToContent')}
      </a>
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        people={people}
      />
      {/* Mobile sidebar overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      {/* ═══ SIDEBAR ═══ */}
      <AppSidebar
        wsConnected={wsConnected}
        wsPresenceWithMe={wsPresenceWithMe}
        queueSize={queueSize}
        qualityIssueCount={qualityAnalysis.issueCount}
        onLogout={handleLogout}
      />
      {/* ═══ MAIN CONTENT (topbar + content area) ═══ */}
      <div className="main-content">
        <AppTopbar
          connectionState={connectionState}
          activeAnnouncements={activeAnnouncements}
          isViewer={isViewer}
          onDismissAnnouncement={dismissAnnouncement}
        >
          <PresenceContext.Provider value={wsPresenceWithMe}>
            <AppRoutes
              people={people}
              relationships={relationships}
              sources={sources}
              currentProject={currentProject}
              projects={projects}
              rootId={rootId}
              treeFocusId={treeFocusId}
              isViewer={isViewer}
              reviewCount={reviewCount}
              user={user}
              personStoryCount={personStoryCount}
              lang={lang}
              dashWidgetsUnlocked={dashWidgetsUnlocked}
              setDashWidgetsUnlocked={setDashWidgetsUnlocked}
              dashShowCatalog={dashShowCatalog}
              setDashShowCatalog={setDashShowCatalog}
              onSelectProject={stableSelectProject}
              onAddPerson={handleAddPerson}
              onTreeNodeClick={handleTreeNodeClick}
              onSelectPerson={setSelectedPersonId}
              onPersonSaved={refreshPeopleAndRelationships}
              onOpenEditor={openEditor}
              onSetProband={stableSetProband}
              onDeleteRelationship={handleDeleteRelationship}
              onDeletePerson={handleDeletePerson}
              onBulkDeletePeople={handleBulkDeletePeople}
              onStoriesChanged={handleStoriesChanged}
              onImportSuccess={handleImportSuccess}
              onRestoreDeleted={handleRestoreDeleted}
              onQualityNavigateToPerson={handleQualityNavigateToPerson}
              onRefreshData={refreshPeopleAndRelationships}
              onNavigateToPersonInTree={navigateToPersonInTree}
              broadcastChange={broadcastChange}
              wsOnDataChanged={wsOnDataChanged}
            />
          </PresenceContext.Provider>
        </AppTopbar>
      </div>{' '}
      {/* end .main-content */}
      <AppModals
        isViewer={isViewer}
        createProjectLoading={createProjectLoading}
        showInactivityWarning={showInactivityWarning}
        remainingSeconds={remainingSeconds}
        onCloseEditor={closeEditor}
        onSavePerson={handleSavePerson}
        onDeletePerson={handleDeletePerson}
        onSetProband={handleSetProband}
        onRefreshData={refreshPeopleAndRelationships}
        onDismissInactivity={dismissWarning}
        onCloseSwitcher={handleCloseSwitcher}
        onSwitcherSelect={handleSwitcherSelect}
        onSwitcherCreate={handleSwitcherCreate}
        onSwitcherImport={handleSwitcherImport}
        onProjectDeleted={handleProjectDeleted}
        onProjectUpdated={handleProjectUpdated}
        onInvitationHandled={handleInvitationHandled}
        onCreateProject={handleCreateProject}
        broadcastChange={broadcastChange}
      />
      {tierLimiet && (
        <UpgradePrompt
          variant="modal"
          requiredTier={(tierLimiet.requiredTier as MembershipTier) || 'pro'}
          feature={limietUitleg(tierLimiet, t)}
          onUpgrade={() => {
            setTierLimiet(null);
            navigate('/account?tab=billing');
          }}
          onClose={() => setTierLimiet(null)}
        />
      )}
      <ProefAfscheid />
    </div>
  );
};

export default App;
